// Generated banner theme for a league card.
//
// A league gets one of ten two-tone themes, chosen by hashing its id. Nothing is
// stored: a league id is already a random uuid, so hashing it is equivalent to
// rolling at creation time, with two advantages. No column is written, and every
// league that already exists gets a banner retroactively. The result is stable
// forever and identical on every device.
//
// Adding an admin override later is one nullable column and one branch in
// bannerTheme(): read leagues.banner_theme when set, fall back to the hash when
// it is not.
//
// These hex values are DERIVED, not hand-picked. scripts/derive-banner-palette.js
// generates them in OKLCH and prints this table; edit the theme definitions there
// and re-run it rather than nudging hex by hand, or the guarantees below stop
// holding. Plain hex is what ships, so nothing at runtime depends on OKLCH
// support, which matters because Capacitor's Android WebView can be old.
//
// What the derivation fixes, and what a hand-picked palette got wrong:
//
//   - Every `left` sits at OKLCH lightness 0.255 and every `right` at 0.375, so
//     all ten split at the same strength (2.8x to 3.6x in relative luminance).
//     The first, eyeballed palette ranged from 1.03x to 6.3x: some cards read as
//     a real two-tone, others as one field with a stripe through it.
//   - The dark half is always `left`, which is the text side, so the league name
//     gets the better contrast on every theme. Worst case is 9.8:1 against the
//     4.5:1 floor.
//   - `Lift` is the top stop of each field's gradient: lightness raised 0.045 and
//     hue rotated 6 degrees toward the accent. Rotating matters. Raising
//     lightness alone in sRGB desaturates toward grey, which is what made the
//     first palette's mid-tones look muddy.
//   - `accent` is the seam line only, pinned to lightness 0.86 and chroma 0.13
//     so it reads as one design element across the set rather than ten decisions.

export const BANNER_THEMES = [
  { id: "steel-maroon",    leftBase: "#012443", leftLift: "#142E50", rightBase: "#6E2523", rightLift: "#7C3327", accent: "#DDCFBC",
    dark: { leftBase: "#335578", leftLift: "#446086", rightBase: "#9C4E49", rightLift: "#AB5D4F", accent: "#DDCFBC" } },
  { id: "ocean-rust",      leftBase: "#00263A", leftLift: "#003150", rightBase: "#692D00", rightLift: "#753C03", accent: "#FFC581",
    dark: { leftBase: "#1F5879", leftLift: "#346489", rightBase: "#9C5223", rightLift: "#A86229", accent: "#FFC581" } },
  { id: "slate-forest",    leftBase: "#192236", leftLift: "#262D42", rightBase: "#134E26", rightLift: "#165B38", accent: "#86EAAB",
    dark: { leftBase: "#485269", leftLift: "#565E76", rightBase: "#3F774D", rightLift: "#45865F", accent: "#86EAAB" } },
  { id: "midnight-teal",   leftBase: "#131F46", leftLift: "#232953", rightBase: "#024C49", rightLift: "#055959", accent: "#5DE9E9",
    dark: { leftBase: "#41517C", leftLift: "#525C8A", rightBase: "#207773", rightLift: "#2F8485", accent: "#5DE9E9" } },
  { id: "charcoal-plum",   leftBase: "#25202D", leftLift: "#312B38", rightBase: "#5C2A5F", rightLift: "#6D3567", accent: "#FEBAD9",
    dark: { leftBase: "#54505F", leftLift: "#625C6B", rightBase: "#87528A", rightLift: "#995E92", accent: "#FEBAD9" } },
  { id: "navy-crimson",    leftBase: "#04214B", leftLift: "#172B58", rightBase: "#722024", rightLift: "#802E27", accent: "#FEC66B",
    dark: { leftBase: "#345381", leftLift: "#465E8F", rightBase: "#A14A4A", rightLift: "#AF594F", accent: "#FEC66B" } },
  { id: "pine-ember",      leftBase: "#012B15", leftLift: "#013823", rightBase: "#653100", rightLift: "#723E01", accent: "#FCC86C",
    dark: { leftBase: "#305E41", leftLift: "#376B52", rightBase: "#9A541B", rightLift: "#A66422", accent: "#FCC86C" } },
  { id: "graphite-volt",   leftBase: "#21222C", leftLift: "#2D2D37", rightBase: "#3F3F51", rightLift: "#4D4A5D", accent: "#C9DD76",
    dark: { leftBase: "#50525D", leftLift: "#5E5E69", rightBase: "#67677A", rightLift: "#767387", accent: "#C9DD76" } },
  { id: "royal-burgundy",  leftBase: "#0A1D52", leftLift: "#1C275F", rightBase: "#6C2630", rightLift: "#7A3335", accent: "#F5CB70",
    dark: { leftBase: "#375089", leftLift: "#4A5B97", rightBase: "#9A4F56", rightLift: "#A95C5C", accent: "#F5CB70" } },
  { id: "lineup-blue",     leftBase: "#01253F", leftLift: "#002E57", rightBase: "#623400", rightLift: "#6E4103", accent: "#95DCFF",
    dark: { leftBase: "#1A5783", leftLift: "#326293", rightBase: "#925A25", rightLift: "#9D692E", accent: "#95DCFF" } },
];

// Returns the theme for a league id, in the variant that suits the page it is
// drawn on. Falls back to the first theme rather than throwing, because a card
// in the wrong colours is a better outcome than a /home page that does not
// render at all.
//
// The mode argument is not cosmetic. The light values are deliberately dark,
// because they carry white text on a white page. On a #1C1C1E card the same
// hex separates from its own background by about 1.4x, so the header stops
// being a band of colour and becomes a slightly different black. The dark
// variant lifts both fields to about 2.2x while keeping white above the 4.5:1
// floor on each.
export function bannerTheme(leagueId, mode = "light") {
  const theme = !leagueId
    ? BANNER_THEMES[0]
    : BANNER_THEMES[Math.abs(hash(leagueId)) % BANNER_THEMES.length];
  return mode === "dark" ? { id: theme.id, ...theme.dark } : theme;
}

function hash(leagueId) {
  let h = 0;
  for (let i = 0; i < leagueId.length; i++) {
    h = (h * 31 + leagueId.charCodeAt(i)) | 0;
  }
  return h;
}
